<template>
    <div @mouseenter="onMouseEnter">
        <!--popup-content-style="width: 1px" rely on popup menu min width as container to have menu not exceed width-->
        <q-select
            ref="dropdown"
            v-tooltip="labelTooltip"
            class="NewDropdown"
            :class="{
                'NewDropdown--hideLabelWhenHasValue': _hideLabelWhenHasValue,
                'NewDropdown--noEllipsis': noEllipsis,
            }"
            :popup-content-class="_popupContentClass"
            popup-content-style="width: 1px"
            input-class="NewDropdown__input"
            color="borderGreen"
            menu-anchor="bottom left"
            outlined
            options-dense
            dense
            :model-value="multiSelect ? selectedOptions : selected"
            :options="filteredOptions"
            :multiple="multiSelect"
            :disable="disabled || (!hasAvailableOptions && !allowEmpty)"
            :label="label"
            :virtual-scroll-item-size="48"
            :clearable="false"
            :menu-offset="[0, 5]"
            :input-style="selectInputStyle"
            v-bind="filteringEnabled ? { useInput: true, inputDebounce: 0 } : {}"
            @update:model-value="handleInput"
            @focus="$emit('focus')"
            @blur="onBlur"
            @clear="clearSelection"
            @popup-show="onPopupShow"
            @popup-hide="onPopupHide"
            v-on="filteringEnabled ? { filter: filterFn } : {}"
        >
            <template v-if="showSelectedItems" #selected-item="scope">
                <slot name="selected-item" v-bind="scope">
                    <q-chip
                        color="chipBackground"
                        text-color="chipText"
                        class="NewDropdown__chip"
                        dense
                        :removable="multiSelect"
                        :tabindex="scope.tabindex"
                        @remove="scope.removeAtIndex(scope.index)"
                    >
                        {{ scope.opt.label }}
                    </q-chip>
                </slot>
            </template>

            <!-- TODO: Move styles to this component instead of having it in MultiSelectOptions -->
            <template v-if="multiSelect" #before-options>
                <div class="MultiSelectOptions__headerActionBar">
                    <div
                        v-if="!hideSelectAll"
                        class="MultiSelectOptions__selectAllButton MultiSelectOptions__textButton"
                        @click="onClickSelectAllMultiSelect"
                    >
                        SELECT ALL
                    </div>
                    <div
                        class="MultiSelectOptions__textButton"
                        :class="{ 'MultiSelectOptions__textButton--pushRight': hideSelectAll }"
                        @click="onClickClearAllMultiSelect"
                    >
                        CLEAR
                    </div>
                </div>
                <slot name="before-options" />
            </template>

            <!-- TODO: Move styles to this component instead of having it in MultiSelectOptions -->
            <template v-if="multiSelect" #after-options>
                <slot name="after-options" />
                <div class="MultiSelectOptions__footerActionBar">
                    <div class="MultiSelectOptions__textButton" @click="onClickAbortMultiSelect">
                        Cancel
                    </div>
                    <div
                        class="MultiSelectOptions__textButton MultiSelectOptions__submitButton"
                        @click="onClickApplyMultiSelect"
                    >
                        Apply
                    </div>
                </div>
            </template>

            <template #prepend>
                <slot name="prepend" />
            </template>

            <template #option="scope">
                <slot name="option" v-bind="scope">
                    <NewDropdownSelectOption
                        :scope="scope"
                        :multi-select="multiSelect"
                    />
                </slot>
            </template>

            <template #no-option>
                <slot name="no-option" :filter-text="filterText">
                    <div class="text-eidSecureBlue-75 p-2">
                        No results
                    </div>
                </slot>
            </template>

            <template #after-menu>
                <slot name="additionalContent" />
            </template>
        </q-select>
    </div>
</template>

<style lang="scss">
$eidBgBlueTones-medium: #d0d8de;
$border-color: $eidBgBlueTones-medium;
$border-color-focus: #2eb495;
$border-radius: 3px;

// Quasar's `.q-field__control` sets `color: var(--q-primary)` at the same specificity,
// so the class is nested under the block to win regardless of stylesheet order.
.NewDropdown .text-borderGreen {
    color: $border-color-focus;
}

.bg-chipBackground {
    background-color: #2ab698;
}

.text-chipText {
    color: #f6fafd;
}

.NewDropdown {
    // We override the q-select style that uses !important
    background: #ffffff !important;
    border-radius: $border-radius;

    // Smaller dropdown arrow (Font Awesome angle-down) so it fits the field
    .q-field__append {
        font-size: 0.75rem;
    }

    &--hideLabelWhenHasValue {
        .q-field__label {
            display: none;
        }

        .q-field__control-container {
            // We override the q-select style that has very heavy specificity
            padding-top: 0 !important;
        }
    }

    // Quasar's `.q-field--outlined .q-field__control` sets a 4px radius at the same
    // specificity, so the radius is repeated with the modifier class to win regardless
    // of stylesheet order. The field is always outlined.
    &.q-field--outlined .q-field__control {
        border-radius: $border-radius;
    }

    .q-field__control {
        border-radius: $border-radius;

        &::before {
            border-color: $border-color;
        }

        &::before, &::after {
            // We override the q-select style that uses !important
            border-width: 1px !important;
        }
    }

    :not(&--noEllipsis) {
        // Every flex item in the Quasar field chain defaults to min-width: auto,
        // preventing shrinking below content width. Override to min-width: 0
        // so that text-overflow: ellipsis can activate on the selected value span.
        .q-field__inner,
        .q-field__control-container,
        .q-field__control-container .q-field__native {
            min-width: 0;
        }

        .q-field__control-container .q-field__native > span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            min-width: 0;
        }

        .NewDropdown__chip .q-chip__content {
            white-space: nowrap !important;
            overflow: hidden;
            text-overflow: ellipsis;
            display: inline-block;
        }
    }

    &__popupContent {
        max-height: 300px;
    }

    &__input {
        height: auto !important;
    }

    &__chip {
        padding: 0.2em 0.5em;
        // We override the q-select style
        height: unset !important;

        .q-chip__content {
            // We override the q-select style
            white-space: unset !important;
        }
    }
}

.MultiSelectOptions__textButton--pushRight {
    margin-left: auto;
}

.body--modalOpened {
    .NewDropdown__popupContent {
        // It's necessary to have a high z-index to avoid being covered by the modal which has 99999 z-index
        z-index: 100000;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { QSelect, QChip } from 'quasar';

    import NewDropdownSelectOption from './NewDropdownSelectOption.vue';
    import { DropdownOption } from './types';

    function sanitizeVal(val: string): string {
        // Remove whitespace and punctuations
        return val.replace(/[\s\p{P}]/gu, '');
    }

    export default defineComponent({
        name: 'NewDropdown',
        components: {
            QSelect,
            QChip,
            NewDropdownSelectOption,
        },
        props: {
            hideSelectedChip: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            persistSelectedItems: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            multiSelect: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            disabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            noEllipsis: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            label: {
                type: String as PropType<string | undefined>,
                default: undefined,
            },
            selected: {
                // Must be an array for multiple selection, and an object | null for single selection
                type: [ Array, Object ] as PropType<DropdownOption[] | DropdownOption | undefined>,
                default: undefined,
            },
            options: {
                type: Array as PropType<DropdownOption[]>,
                default: (): DropdownOption[] => [],
            },
            popupContentClass: {
                type: String as PropType<string | undefined>,
                default: undefined,
            },
            hideLabelWhenHasValue: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
            minOptionsForFilter: {
                type: Number as PropType<number>,
                default: 10,
            },
            // Some inputs don't have predefined options and must allow users to create values dynamically (e.g., tags).
            allowEmpty: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            hideSelectAll: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'blur', 'change', 'clear', 'close', 'focus', 'input', 'open', 'search-input', 'select' ],
        data() {
            return {
                // Only used for multiple selection
                selectedOptions: [] as DropdownOption[],
                // Only used for multiple selection
                previousOptions: [] as DropdownOption[],
                filterRegex: null as RegExp | null,
                filterText: '' as string,
                // Inline style for the input of the q-select component when filtering is enabled
                selectInputStyle: {
                    display: 'none !important',
                } as any,
                popupIsShown: false,
                labelTooltip: null as string | null,
            };
        },
        computed: {
            _popupContentClass(): string {
                return [ 'NewDropdown__popupContent', this.popupContentClass ]
                    .filter(Boolean).join(' ');
            },
            showSelectedItems(): boolean {
                if (this.hideSelectedChip) {
                    return false;
                }
                return this.popupIsShown || this.persistSelectedItems;
            },
            qSelectRef(): QSelect | null {
                return this.$refs.dropdown as QSelect | null;
            },
            hasValue(): boolean {
                return this.multiSelect ? this.selectedOptions?.length > 0 : this.selected != null;
            },
            _hideLabelWhenHasValue(): boolean {
                return this.hasValue && this.hideLabelWhenHasValue;
            },
            filteringEnabled(): boolean {
                return this.availableOptions.length >= this.minOptionsForFilter;
            },
            // The implementation of this is a bit complex, but it's necessary to avoid unnecessary re-computations
            availableOptions(): DropdownOption[] {
                // When single selection, we need to exclude the selected value
                const selected = this.selected;
                const shouldExcludeSelected = selected && !this.multiSelect;
                if (shouldExcludeSelected) {
                    return this.options.filter(({ value }) => value !== (selected as DropdownOption).value);
                }
                return this.options;
            },
            filteredOptions(): DropdownOption[] {
                const filterRegex = this.filterRegex;
                return filterRegex ?
                    this.availableOptions.filter(({ label }) => filterRegex.test(sanitizeVal(label)))
                    : this.availableOptions;
            },
            hasAvailableOptions(): boolean {
                return this.availableOptions.length > 0;
            },
        },
        watch: {
            selected: {
                immediate: true,
                handler(newValue) {
                    if (this.multiSelect) {
                        this.selectedOptions = newValue;
                        this.previousOptions = newValue;
                    }
                },
            },
        },
        methods: {
            onMouseEnter() {
                if (this.noEllipsis) {
                    return;
                }
                this.updateLabelTooltip();
            },
            close(): void {
                this.qSelectRef?.hidePopup();
            },
            focusInput(): void {
                this.$nextTick(() => {
                    const input = this.qSelectRef?.$el.querySelector('input[type="search"]') as HTMLInputElement | null;
                    input?.focus();
                });
            },
            clearFilter(): void {
                this.filterText = '';
                this.filterRegex = null;
                this.$emit('search-input', '');
                this.qSelectRef?.updateInputValue('');
                this.focusInput();
            },
            onPopupHide(): void {
                this.popupIsShown = false;
                this.filterText = '';
                this.filterRegex = null;
                this.$emit('search-input', '');
                if (this.filteringEnabled) {
                    // We override the inline style because the q-select component uses !important
                    this.selectInputStyle = {
                        display: 'none !important',
                    };
                }
                this.$emit('close');
            },
            onPopupShow(): void {
                this.popupIsShown = true;
                if (this.filteringEnabled) {
                    // We override the inline style because the q-select component uses !important
                    this.selectInputStyle = {
                        display: 'block !important',
                    };

                    this.$nextTick(() => {
                        const input = this.qSelectRef?.$el.querySelector('input[type="search"]') as HTMLInputElement | null;
                        input?.focus();
                    });
                }
                this.$emit('open');
                this.$emit('focus');
            },
            handleInput(value: DropdownOption | DropdownOption[]): void {
                if (this.multiSelect) {
                    this.onMultiSelect(value as DropdownOption[]);
                    this.clearFilter();
                } else {
                    this.onSingleSelect(value as DropdownOption);
                }
            },
            onSingleSelect(option: DropdownOption): void {
                this.$emit('select', option);
                this.$emit('input', option);
                this.$emit('change', option);
            },
            onMultiSelect(options: DropdownOption[]): void {
                this.selectedOptions = options ? options : [];
                this.$emit('select', options);
            },
            onClickAbortMultiSelect(): void {
                this.close();
                this.selectedOptions = this.previousOptions;
            },
            onClickApplyMultiSelect(): void {
                this.applySelectionsMultiSelect();
                this.close();
            },
            onClickSelectAllMultiSelect(): void {
                this.onMultiSelect(this.availableOptions.filter(({ disable }) => !disable));
            },
            onClickClearAllMultiSelect(): void {
                this.onMultiSelect([]);
                this.$emit('clear');
                this.focusInput();
            },
            onBlur(): void {
                if (this.multiSelect) {
                    this.applySelectionsMultiSelect();
                }
                this.$emit('blur');
            },
            clearSelection(): void {
                const value = this.multiSelect ? [] : null;
                this.$emit('select', value);
                this.$emit('input', value);
                this.$emit('change', value);
                this.$emit('clear');
            },
            applySelectionsMultiSelect(): void {
                this.previousOptions = this.selectedOptions;
                this.$emit('input', this.selectedOptions);
                this.$emit('change', this.selectedOptions);
            },
            filterFn(val: any, update: any, _abort: any): void {
                this.filterText = val;
                this.$emit('search-input', val);
                update(() => {
                    if (val.trim()) {
                        const sanitizedVal = sanitizeVal(val);
                        // ensure search is case-insensitive
                        const regex = new RegExp(sanitizedVal, 'iu');
                        this.filterRegex = regex;
                    } else {
                        // If input is empty or just whitespace, clear the filter
                        this.filterRegex = null;
                    }
                });
            },
            getSingleSelectLabel(selected: DropdownOption | DropdownOption[] | undefined): string {
                return (selected as DropdownOption | undefined)?.label ?? '';
            },
            updateLabelTooltip() {
                if (this.noEllipsis) {
                    this.labelTooltip = null;
                    return;
                }

                this.$nextTick(() => {
                    const el = this.qSelectRef?.$el.querySelector(
                        '.q-field__control-container .q-field__native > span',
                    ) as HTMLElement | null;

                    if (!el) {
                        this.labelTooltip = null;
                        return;
                    }
                    const overflowed = el.scrollWidth > el.clientWidth;
                    const fullText = this.getSingleSelectLabel(this.selected);
                    this.labelTooltip = overflowed && fullText ? fullText : null;
                });
            },
        },
    });
</script>
